import { Controller } from "@hotwired/stimulus";
import { effect } from "@preact/signals-core";
import type MapBoard from "./map_board_controller";
import type { LabelDraft } from "./map_board_controller";

const GAP = 12;
// Tailwind's max-sm: below it the editor is a bottom sheet, placed by CSS.
const SHEET = "(max-width: 639px)";

// Connects to data-controller="map-label", beside map-board on the master's
// board root. The board opens this small editor where the Texto mode taps;
// the board keeps the label and writes it, and this only shows the field.
export default class extends Controller {
	static targets = ["panel", "input"];
	declare readonly panelTarget: HTMLFormElement;
	declare readonly inputTarget: HTMLInputElement;
	label?: LabelDraft | null;
	dispose?: () => void;
	refocusOnClick?: () => void;

	disconnect() {
		this.close();
	}

	get board() {
		return this.application.getControllerForElementAndIdentifier(this.element, "map-board") as MapBoard | null;
	}

	open(label: LabelDraft) {
		this.close();
		this.label = label;
		this.inputTarget.value = label.text;
		this.panelTarget.hidden = false;
		// A new label's draft follows the tab's size, and the panel with it.
		this.dispose = effect(() => {
			this.board.state.local.viewport.value;
			this.board.state.local.drafts.value;
			this.place();
		});
		this.board.renderer?.reveal({ x: label.points[0], y: label.points[1] }, (label.width * this.board.state.local.viewport.peek().zoom) / 2 + GAP);
		this.refocus();
		this.inputTarget.select();
		// A touch's mousedown comes after the tap that opened this, and would
		// focus the map; the click that ends it hands the focus back.
		this.refocusOnClick ??= () => this.refocus();
		window.addEventListener("click", this.refocusOnClick, { once: true, capture: true });
	}

	refocus() {
		if (this.label) this.inputTarget.focus({ preventScroll: true });
	}

	close() {
		if (!this.label) return;
		this.dispose?.();
		this.dispose = null;
		this.label = null;
		window.removeEventListener("click", this.refocusOnClick, { capture: true });
		this.panelTarget.hidden = true;
		if (this.panelTarget.contains(document.activeElement)) this.board?.canvasTarget.focus({ preventScroll: true });
	}

	preview() {
		this.board?.previewLabel(this.inputTarget.value);
	}

	save() {
		this.board?.saveLabel(this.inputTarget.value);
	}

	dismiss() {
		this.board?.closeLabel();
	}

	// Centred under the label, above it when the board runs out, and clamped;
	// positions are the frame's, which keeps clear of the screen's notches.
	place() {
		const { points, width } = this.board.label ?? this.label;
		const at = this.board.renderer?.imageToClient({ x: points[0], y: points[1] });
		if (!at || window.matchMedia(SHEET).matches) return;
		const frame = (this.panelTarget.offsetParent ?? this.element).getBoundingClientRect();
		const panel = this.panelTarget.getBoundingClientRect();
		const half = (width * this.board.state.local.viewport.peek().zoom) / 2;
		let top = at.y - frame.top + half + GAP;
		if (top + panel.height > frame.height - GAP) top = at.y - frame.top - half - GAP - panel.height;
		top = Math.min(Math.max(top, GAP), Math.max(frame.height - panel.height - GAP, GAP));
		const left = Math.min(Math.max(at.x - frame.left - panel.width / 2, GAP), Math.max(frame.width - panel.width - GAP, GAP));
		this.panelTarget.style.left = `${left}px`;
		this.panelTarget.style.top = `${top}px`;
	}
}
